import React, { useState } from "react";
import { Button } from "../../@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import ReviewProjectDialog from "@/forms/reviewProjectDialog";
import AiGeneratedReviewDialog from "@/components/AiGeneratedReviewDialog";
import AssignSubmissionReviewerDialog from "@/forms/assignSubmissionReviewerDialog";
import useProjectAiReview from "@/hooks/useProjectAiReview";
import { useAuth, useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { getUserTimezone, formatUtcToUserTimezone } from "../lib/timezone";

const UnreviewedCard = ({
  submission,
  courseSlug,
  courseName,
  userFirstName,
  userLastName,
  userEmail,
  reviewerFirstName,
  reviewerLastName,
  topicTitle,
  refetchReviewedProjects,
  viewerIsReviewer = false,
}) => {
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [assignReviewerOpen, setAssignReviewerOpen] = useState(false);
  const { userId } = useAuth();
  const { user, isLoaded } = useUser();

  const reviewerLabel = React.useMemo(() => {
    const explicit = `${reviewerFirstName || ""} ${reviewerLastName || ""}`.trim();
    if (explicit) return explicit;
    if (!viewerIsReviewer) return "";

    const selfName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    if (selfName) return selfName;
    if (isLoaded) return "You";
    return "";
  }, [isLoaded, reviewerFirstName, reviewerLastName, user?.firstName, user?.lastName, viewerIsReviewer]);

  const currentAssignedReviewerId = React.useMemo(() => {
    const s = submission;
    const candidates = [
      s?.assigned_reviewer,
      s?.assignedReviewer,
      s?.submission?.assigned_reviewer,
      s?.submission?.assignedReviewer,
    ];
    const found = candidates.find(
      (v) => v !== null && v !== undefined && String(v).trim() !== ""
    );
    return found != null ? String(found) : "";
  }, [submission]);

  const userTimezone = React.useMemo(() => getUserTimezone(), []);

  const formatDateForDisplay = (value) =>
    formatUtcToUserTimezone(value, userTimezone);
  

  const resolvedIds = React.useMemo(() => {
    const s = submission;

    const resolvedStudentId =
      s?.student_id ??
      s?.studentId ??
      s?.user_id ??
      s?.userId ??
      s?.submission?.user_id ??
      s?.submission?.userId ??
      s?.user?.id ??
      s?.student?.user_id ??
      s?.student?.id ??
      s?.student?._id ??
      null;

    const resolvedProjectId =
      s?.project_id ??
      s?.projectId ??
      s?.submission?.project_id ??
      s?.submission?.projectId ??
      s?.project?.id ??
      s?.project?._id ??
      null;

    return {
      studentId: resolvedStudentId,
      projectId: resolvedProjectId,
    };
  }, [submission]);

  const {
    aiReviewText,
    hasAIReview,
    latestAIReview,
    loading: aiReviewLoading,
    deleting: aiReviewDeleting,
    rejectLatest: rejectLatestAIReview,
  } = useProjectAiReview({
    projectId: resolvedIds.projectId,
    studentId: resolvedIds.studentId,
    enabled: aiReviewOpen,
  });

  const handleAssignNewReviewer = () => {
    setAssignReviewerOpen(true);
  };

  const handleUseAiReviewFromCard = async () => {
    if (!hasAIReview) return;
    try {
      await navigator.clipboard.writeText(aiReviewText);
      toast.success("AI review copied to clipboard");
    } catch (err) {
      console.error("Failed to copy AI review to clipboard:", err);
      toast.error("Failed to copy AI review");
    } finally {
      setAiReviewOpen(false);
    }
  };

  const handleRejectAiReviewFromCard = async () => {
    const ok = await rejectLatestAIReview();
    if (ok) setAiReviewOpen(false);
  };

  return (
    <div className="bg-(--surface-base) border border-(--neutral-300) rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-lg text-(--neutral-900) font-heading">
              {courseSlug ? (
                <span className="font-heading uppercase">
                  {courseSlug} -{" "}
                </span>
              ) : null}

              {/* {topicTitle ? (
                <span className="font-heading">
                  {topicTitle} -{" "}
                </span>
              ) : null} */}

              {submission?.project?.title || (
                <span className="text-(--neutral-400)">No title available</span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {/* <Badge
                            variant="outline"
                            className="border-2 text-red-600 border-red text-sm p-1.5 rounded-full font-heading"
                        >
                            {project.category || "Not available"}
                        </Badge> */}

            <div className="text-sm text-(--neutral-700)">
              <span className="font-medium font-body text-(--neutral-700)">
                {userFirstName || "Name Not available"}{" "}
                {userLastName || "Not available"}
              </span>{" "}
              •
              <span className="ml-1 font-body text-(--neutral-700)">
                Submitted {" "}
                {formatDateForDisplay(submission.submitted_on)}
              </span>

              <span className="ml-1">•</span>
              <span className="ml-1 font-body text-(--neutral-700)">
                To be reviewed by{" "}
                <span className="font-medium">
                  {reviewerLabel || "Not assigned"}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-stretch gap-0.5">
            <ReviewProjectDialog
              submission={submission}
              gradedBy={userId}
              onReviewSubmitted={refetchReviewedProjects}
              courseName={courseName}
              topicTitle={topicTitle}
              userFirstName={userFirstName}
              userLastName={userLastName}
              userEmail={userEmail}
              triggerClassName="rounded-l-md rounded-r-none"
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-none w-12 md:w-auto rounded-l-none rounded-r-md px-0 md:px-3 flex items-center justify-center"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-50">
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    handleAssignNewReviewer();
                  }}
                >
                  Assign New Reviewer
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setAiReviewOpen(true);
                  }}
                >
                  View Rukia AI Review
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <AiGeneratedReviewDialog
        open={aiReviewOpen}
        onOpenChange={setAiReviewOpen}
        loading={aiReviewLoading}
        deleting={aiReviewDeleting}
        hasAIReview={hasAIReview}
        aiReviewText={aiReviewText}
        canReject={Boolean(latestAIReview?.id)}
        onReject={handleRejectAiReviewFromCard}
        onApproveUse={handleUseAiReviewFromCard}
      />

      <AssignSubmissionReviewerDialog
        open={assignReviewerOpen}
        onOpenChange={setAssignReviewerOpen}
        submissionUserId={resolvedIds.studentId}
        submissionProjectId={resolvedIds.projectId}
        currentReviewerId={currentAssignedReviewerId}
        onAssigned={(assignment) => refetchReviewedProjects(assignment)}
      />
    </div>
  );
};

export default UnreviewedCard;
